import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Moon, Sun, MonitorSmartphone } from 'lucide-react';
import { useUIStore, applyThemeToDocument } from '@/stores/useUIStore';

/**
 * Keeps <html> in step with the theme store on every page change: the sign-in
 * screens and the customer screen always stay light (see isLightOnlyPath).
 */
export function ThemeSync() {
  const { pathname } = useLocation();
  const isDarkMode = useUIStore((s) => s.isDarkMode);
  useEffect(() => {
    applyThemeToDocument(isDarkMode, { pathname });
  }, [isDarkMode, pathname]);
  return null;
}

/** The sun / moon button of the top bars (desktop and phone). */
export function ThemeToggle({ className = '', size = 'md' }) {
  const { t } = useTranslation();
  const isDarkMode = useUIStore((s) => s.isDarkMode);
  const toggle = useUIStore((s) => s.toggleDarkMode);
  const label = isDarkMode ? t('Switch to light theme') : t('Switch to dark theme');
  const box = size === 'sm' ? 'h-9 w-9' : 'h-10 w-10';
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={label}
      title={label}
      aria-pressed={isDarkMode}
      data-testid="theme-toggle"
      className={`ods-theme-toggle relative inline-flex ${box} shrink-0 items-center justify-center rounded-lg border transition-colors ${
        isDarkMode
          ? 'border-[#5A6ACF]/40 bg-[#5A6ACF]/15 text-amber-300 hover:bg-[#5A6ACF]/25'
          : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-100 hover:text-[#5A6ACF]'
      } focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5A6ACF] focus-visible:ring-offset-2 ${className}`}
    >
      {isDarkMode ? <Sun className="h-5 w-5" aria-hidden="true" /> : <Moon className="h-5 w-5" aria-hidden="true" />}
    </button>
  );
}

/** « Theme: Light / Dark / Like the device » — profile menus and settings. */
export function ThemeChoice({ className = '', compact = false }) {
  const { t } = useTranslation();
  const pref = useUIStore((s) => s.themePreference);
  const setPref = useUIStore((s) => s.setThemePreference);
  const options = [
    { id: 'light', label: t('Light (theme)'), Icon: Sun },
    { id: 'dark', label: t('Dark (theme)'), Icon: Moon },
    { id: 'system', label: t('Like the device'), Icon: MonitorSmartphone },
  ];
  return (
    <div className={className} data-testid="theme-choice">
      <p className={`mb-1.5 font-semibold text-slate-500 ${compact ? 'text-[11px]' : 'text-xs'}`} id="ods-theme-choice-label">
        {t('Theme')}
      </p>
      <div role="radiogroup" aria-labelledby="ods-theme-choice-label" className="grid grid-cols-3 gap-1 rounded-xl bg-slate-100 p-1 ods-theme-choice">
        {options.map(({ id, label, Icon }) => {
          const on = pref === id;
          return (
            <button
              key={id}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={(e) => { e.stopPropagation(); setPref(id); }}
              className={`flex min-w-0 flex-col items-center gap-0.5 rounded-lg px-1 py-1.5 text-[11px] font-semibold leading-tight transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5A6ACF] ${
                on ? 'bg-[#5A6ACF] text-white shadow-sm ods-theme-choice-on' : 'text-slate-600 hover:bg-white'
              }`}
            >
              <Icon className="h-4 w-4" aria-hidden="true" />
              <span className="w-full break-words text-center">{label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
